import { useState, useEffect } from 'react';
import { tokenManager } from '../config/authConfig';
import API_CIFRA from '../config/Variables_apis';

/**
 * Catálogo de vías/calles (`GET /api/via/`, pública sin login vía
 * `PublicAccessMiddleware`). La usa `PasoUbicacion.jsx` como método
 * auxiliar de ubicación en el reporte ciudadano ("elegir una vía
 * registrada" en vez de buscar dirección/arrastrar el pin) — `cargarVias`
 * carga el catálogo completo al montar, y admite un `search` opcional para
 * filtrar por nombre mientras el ciudadano escribe.
 */
const useVias = () => {
  const [vias, setVias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Función para obtener headers de autenticación
  const getAuthHeaders = () => {
    const token = tokenManager.getAccessToken();
    const headers = {
      "Content-Type": "application/json",
    };
    
    if (token && !tokenManager.isTokenExpired()) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Función para manejar errores específicos
  const handleApiError = (error, response) => {
    let errorMessage = "Error al cargar las vías";
    
    if (response?.status === 400) {
      errorMessage = "Datos inválidos.";
    } else if (response?.status === 401) {
      errorMessage = "Error de autenticación.";
    } else if (response?.status === 403) {
      errorMessage = "No tienes permisos para acceder a las vías.";
    } else if (response?.status === 404) {
      errorMessage = "Vías no encontradas.";
    } else if (response?.status === 500) {
      errorMessage = "Error interno del servidor.";
    }
    
    return errorMessage;
  };

  // Función para cargar vías. `search` es opcional (filtra por nombre/
  // descripción en el backend, mismo parámetro que `listar_vias`) — sin él,
  // trae el catálogo completo, como al montar el hook.
  const cargarVias = async (search = '') => {
    setLoading(true);
    setError(null);

    try {
      const url = search
        ? `${API_CIFRA.via.listar}?search=${encodeURIComponent(search)}`
        : API_CIFRA.via.listar;
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = handleApiError(null, response);
        throw new Error(data.error || data.detail || errorMessage);
      }

      // Asegurar que data sea siempre un array
      let viasArray = [];
      
      if (Array.isArray(data)) {
        viasArray = data;
      } else if (data && typeof data === 'object') {
        // Si es un objeto, intentar extraer el array de vías (formato IDÉNTICO a categorías)
        if (data.vias && Array.isArray(data.vias)) {
          viasArray = data.vias;
        } else if (data.results && Array.isArray(data.results)) {
          viasArray = data.results;
        } else if (data.data && Array.isArray(data.data)) {
          viasArray = data.data;
        }
      }
      
      setVias(viasArray);
      return { success: true, data: viasArray };

    } catch (error) {
      const errorMessage = error.message || "Error al cargar vías";
      console.error('Error en useVias:', errorMessage);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener vía por ID
  const obtenerViaPorId = (id) => {
    return vias.find(via => via.id === id);
  };

  // Función para obtener vía por nombre
  const obtenerViaPorNombre = (nombre) => {
    return vias.find(via => via.via_chNombre === nombre);
  };

  // Cargar vías al montar el hook
  useEffect(() => {
    cargarVias();
  }, []);

  return {
    vias,
    loading,
    error,
    cargarVias,
    obtenerViaPorId,
    obtenerViaPorNombre
  };
};

export default useVias;
